import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, UserContext } from '@/App';
import { Search, MapPin, User, ChevronRight, Star, Clock, TrendingUp, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import * as LucideIcons from 'lucide-react';
import BottomNav from '@/components/BottomNav';
import ChatBot from '@/components/ChatBot';
import LocationSelector from '@/components/LocationSelector';

// Haversine formula to calculate distance between two points
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user, login, cart } = useContext(UserContext);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [userLocation, setUserLocation] = useState(() => {
    const lat = localStorage.getItem('userLocationLat');
    const lng = localStorage.getItem('userLocationLng');
    const label = localStorage.getItem('userLocationLabel');
    if (lat && lng) {
      return { lat: parseFloat(lat), lng: parseFloat(lng), label: label || 'Selected Location' };
    }
    return null;
  });

  useEffect(() => {
    fetchCategories();
    fetchVendors();
    fetchRecommendations();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
      setFeaturedVendors(response.data.filter(v => v.is_featured || v.discount_percent > 0).slice(0, 6));
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter vendors based on location (8km radius)
  const getFilteredVendorsByLocation = (vendorList) => {
    if (!userLocation) return [];
    
    return vendorList.filter(vendor => {
      if (vendor.lat == null || vendor.lng == null) return false;
      const distance = calculateDistance(
        userLocation.lat, userLocation.lng,
        vendor.lat, vendor.lng
      );
      return distance <= 8;
    });
  };

  const handleLocationSelect = (location) => {
    setUserLocation(location);
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/menu-items/recommendations?limit=6`, { withCredentials: true });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  };

  const handleCategoryClick = (categoryName) => {
    navigate(`/search?category=${encodeURIComponent(categoryName)}`);
  };

  const handleSearch = () => {
    navigate('/search');
  };

  // First filter by location (8km), then by other filters
  const locationFilteredVendors = userLocation ? getFilteredVendorsByLocation(vendors) : vendors;
  
  const filteredVendors = locationFilteredVendors.filter(vendor => {
    if (activeFilter === 'veg') return vendor.categories.some(c => ['South Indian', 'North Indian', 'Salad'].includes(c));
    if (activeFilter === 'non-veg') return vendor.categories.some(c => ['Chinese', 'Shawarma'].includes(c));
    if (activeFilter === 'new') return vendor.rating >= 4.5;
    if (activeFilter === 'popular') return vendor.rating >= 4.3 && vendor.reviews_count > 50;
    if (activeFilter === 'beverages') return vendor.categories.some(c => ['Beverages', 'Cold Coffee'].includes(c));
    return true;
  });

  // Filter featured vendors by location too
  const locationFilteredFeatured = userLocation ? getFilteredVendorsByLocation(featuredVendors) : featuredVendors;

  // Define veg and non-veg categories
  const vegCategories = ['Chhole Bhature', 'Vada Pav', 'Misal Pav', 'Pani Puri', 'Idli & Dosa', 'Samosa', 'Cold Coffee', 'Waffles', 'Ice Cream', 'Pizza', 'Salad', 'Desserts'];
  const nonVegCategories = ['Burger', 'Biryani', 'Chinese', 'Rolls', 'Egg Bhurji Pav'];
  const beverageCategories = ['Cold Coffee'];

  // Filter categories based on active filter
  const filteredCategories = categories.filter(category => {
    if (activeFilter === 'veg') return vegCategories.includes(category.name);
    if (activeFilter === 'non-veg') return nonVegCategories.includes(category.name);
    if (activeFilter === 'beverages') return beverageCategories.includes(category.name);
    return true;
  });

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const IconComponent = ({ iconName }) => {
    const Icon = LucideIcons[iconName] || LucideIcons.UtensilsCrossed;
    return <Icon className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Location Selector Modal */}
      <LocationSelector 
        isOpen={showLocationSelector} 
        onClose={() => setShowLocationSelector(false)}
        onSelectLocation={handleLocationSelect}
      />

      {/* Top Navigation */}
      <div className="navbar-fixed">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold gradient-text">FoodMatrix</h1>
              <button 
                onClick={() => setShowLocationSelector(true)}
                className="flex items-center gap-1 text-sm text-gray-600 mt-1 hover:text-green-600 transition-colors"
                data-testid="location-selector-btn"
              >
                <MapPin className="w-4 h-4 text-green-600" />
                <span className="max-w-[200px] truncate">
                  {userLocation ? userLocation.label : 'Select delivery location'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => user ? navigate('/profile') : login()}
                className="p-2 hover:bg-green-50 rounded-full transition-colors"
                data-testid="profile-button"
              >
                {user && user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-6 h-6 text-green-700" />
                )}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative" onClick={handleSearch}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for dishes, vendors, cuisines..."
              className="pl-10 pr-4 py-6 rounded-xl border-2 border-green-100 focus:border-green-400 cursor-pointer"
              readOnly
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      <div className="pt-40 max-w-7xl mx-auto px-4">
        {/* Quick Filters */}
        <div className="mb-6 flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {[
            { id: 'all', label: 'All', icon: LucideIcons.Grid3x3 },
            { id: 'veg', label: 'Veg', icon: LucideIcons.Leaf },
            { id: 'non-veg', label: 'Non-Veg', icon: LucideIcons.Drumstick },
            { id: 'popular', label: 'Popular', icon: TrendingUp },
            { id: 'new', label: 'New', icon: LucideIcons.Sparkles },
            { id: 'beverages', label: 'Beverages', icon: LucideIcons.Coffee },
          ].map((filter) => {
            const FilterIcon = filter.icon;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`filter-button px-4 py-2 rounded-full font-medium border flex items-center gap-2 whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'active bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-green-200 hover:border-green-400'
                }`}
                data-testid={`filter-${filter.id}`}
              >
                <FilterIcon className="w-4 h-4" />
                {filter.label}
              </button>
            );
          })}
        </div>

        {/* Categories Horizontal Scroll */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 text-gray-800">What's on your mind?</h2>
          <div className="category-scroll" data-testid="category-scroll">
            {filteredCategories.map((category, index) => (
              <div
                key={index}
                className="category-item"
                onClick={() => handleCategoryClick(category.name)}
                data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex flex-col items-center gap-2 w-24">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-white shadow-md border-2 border-green-100">
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="image-cover"
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center leading-tight">
                    {category.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations Section */}
        {recommendations.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                {user ? 'Recommended for You' : 'Popular Dishes'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {recommendations.map((item) => (
                <div
                  key={item.id}
                  className="food-card bg-white rounded-xl overflow-hidden shadow-md cursor-pointer"
                  onClick={async () => {
                    const vendor = await axios.get(`${API}/vendors/${item.vendor_id}`);
                    navigate(`/vendor/${item.vendor_id}`);
                  }}
                  data-testid={`rec-item-${item.id}`}
                >
                  <div className="h-32 overflow-hidden bg-gray-200">
                    <img src={item.image_url} alt={item.name} className="image-cover" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm mb-1 text-gray-800 line-clamp-1">{item.name}</h3>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-green-600">₹{item.price}</span>
                      <span className="text-yellow-500 flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-yellow-500" />
                        {item.rating}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommended Restaurants */}
        {vendors.length > 0 && (
          <div className="mb-8" data-testid="recommended-restaurants-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Star className="w-5 h-5 text-green-600" />
                Recommended Restaurants
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {vendors.slice(0, 12).map((vendor) => (
                <div
                  key={vendor.id}
                  className="food-card bg-white rounded-xl overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/vendor/${vendor.id}`)}
                  data-testid={`restaurant-card-${vendor.id}`}
                >
                  <div className="h-32 overflow-hidden bg-gray-200">
                    <img
                      src={vendor.image_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400'}
                      alt={vendor.name}
                      className="image-cover"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400'; }}
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm mb-1 text-gray-800 line-clamp-1">{vendor.name}</h3>
                    {vendor.location && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-1 line-clamp-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />{vendor.location}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-yellow-500 flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-yellow-500" />
                        {vendor.rating || '4.0'}
                      </span>
                      {vendor.delivery_time && (
                        <span className="text-gray-500 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" />{vendor.delivery_time}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured Offers */}
        {locationFilteredFeatured.length > 0 && userLocation && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Featured Deals</h2>
              <button className="text-green-600 text-sm font-semibold flex items-center gap-1 hover:underline">
                See all <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locationFilteredFeatured.map((vendor) => (
                <div
                  key={vendor.id}
                  className="food-card bg-white rounded-2xl overflow-hidden shadow-lg cursor-pointer relative"
                  onClick={() => navigate(`/vendor/${vendor.id}`)}
                  data-testid={`featured-vendor-${vendor.id}`}
                >
                  {vendor.discount_percent > 0 && (
                    <div className="discount-badge">
                      {vendor.discount_percent}% OFF
                    </div>
                  )}
                  <div className="h-48 overflow-hidden bg-gray-200">
                    <img src={vendor.image_url} alt={vendor.name} className="image-cover" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-1 text-gray-800">{vendor.name}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{vendor.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-semibold">{vendor.rating}</span>
                        <span className="text-gray-500">({vendor.reviews_count})</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{vendor.delivery_time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Vendors */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">All Restaurant Food</h2>
          </div>

          {!userLocation ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <MapPin className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-gray-600 mb-4">Select delivery location to see nearby restaurants</p>
              <Button
                onClick={() => setShowLocationSelector(true)}
                className="btn-primary text-white px-6 py-2 rounded-full"
                data-testid="select-location-prompt-btn"
              >
                Select Location
              </Button>
            </div>
          ) : loading ? (
            <div className="text-center py-12 text-gray-500">Loading vendors...</div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">No restaurants within 8 km of your location</p>
              <p className="text-sm text-gray-400 mb-4">Try selecting a different location</p>
              <Button
                onClick={() => setShowLocationSelector(true)}
                className="bg-green-100 text-green-700 px-6 py-2 rounded-full hover:bg-green-200"
              >
                Change Location
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVendors.map((vendor) => (
                <div
                  key={vendor.id}
                  className="food-card bg-white rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                  onClick={() => navigate(`/vendor/${vendor.id}`)}
                  data-testid={`vendor-card-${vendor.id}`}
                >
                  <div className="h-48 overflow-hidden bg-gray-200">
                    <img src={vendor.image_url} alt={vendor.name} className="image-cover" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-1 text-gray-800">{vendor.name}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{vendor.description}</p>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-semibold">{vendor.rating}</span>
                      </div>
                      <span className="text-gray-600">{vendor.delivery_time}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {vendor.categories.slice(0, 3).map((cat, idx) => (
                        <span key={idx} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
      <ChatBot />
    </div>
  );
};

export default HomePage;